import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { bffPermissionService, bffConfigService } from '@resort-os/api-client';
import { filterAppsBySystemConfig } from '@resort-os/config';
import HeaderPageSearch from '../search/HeaderPageSearch';
import HeaderThemeNotice from '../search/HeaderThemeNotice';

export interface PermissionContextType {
  permissions: string[];
  apps: string[];
  isLoading: boolean;
  hasPermission: (permission: string) => boolean;
  hasApp: (appKey: string) => boolean;
  refetch: () => Promise<void>;
}

export const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

interface PermissionProviderProps {
  children: React.ReactNode;
  role: string;
}

export function PermissionProvider({ children, role }: PermissionProviderProps) {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [apps, setApps] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    if (!role) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const [permsRes, appsRes] = await Promise.all([
        bffPermissionService.getPermissionsForRole(role),
        bffPermissionService.getAppsForRole(role),
      ]);

      const roleApps: string[] = appsRes.data.apps || [];

      let filteredApps = roleApps;
      try {
        const configsRes = await bffConfigService.getAll();
        filteredApps = filterAppsBySystemConfig(roleApps, configsRes.configs || []);
      } catch {
        // config fetch failed — skip filtering, show all role apps
      }

      setPermissions(permsRes.data.permissions || []);
      setApps(filteredApps);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      setPermissions([]);
      setApps([]);
    } finally {
      setIsLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  useEffect(() => {
    const handleConfigUpdated = () => {
      fetchPermissions();
    };
    window.addEventListener('resort-config-updated', handleConfigUpdated);
    return () => window.removeEventListener('resort-config-updated', handleConfigUpdated);
  }, [fetchPermissions]);

  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (role === 'admin') return true;
      return permissions.includes(permission);
    },
    [permissions, role],
  );

  const hasApp = useCallback(
    (appKey: string): boolean => {
      if (role === 'admin') return true;
      return apps.includes(appKey);
    },
    [apps, role],
  );

  const refetch = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  return (
    <PermissionContext.Provider
      value={{
        permissions,
        apps,
        isLoading,
        hasPermission,
        hasApp,
        refetch,
      }}
    >
      {children}
      <HeaderPageSearch />
      <HeaderThemeNotice />
    </PermissionContext.Provider>
  );
}

export function usePermissions(): PermissionContextType {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return ctx;
}

export function usePermission(permission: string): boolean {
  const { hasPermission } = usePermissions();
  return hasPermission(permission);
}
